import { supabase } from "@/lib/supabase";

/**
 * Davetiye kodunu doğrula
 * @param {string} code - Davetiye kodu
 * @returns {Promise<{valid: boolean, invitation: object|null}>}
 */
export async function validateInvitationCode(code) {
  const { data, error } = await supabase
    .from("staff_invitations")
    .select("*")
    .eq("code", code)
    .eq("is_used", false)
    .gt("expires_at", new Date().toISOString())
    .maybeSingle();

  if (error) {
    console.error("Davetiye doğrulama hatası:", error);
    return { valid: false, invitation: null };
  }

  return { valid: !!data, invitation: data };
}

/**
 * Davetiye kodunu kullanıldı olarak işaretle
 * @param {string} invitationId - Davetiye ID
 * @param {string} userId - Kullanan kullanıcı ID
 */
export async function markInvitationUsed(invitationId, userId) {
  const { error } = await supabase
    .from("staff_invitations")
    .update({ is_used: true, used_by: userId })
    .eq("id", invitationId);

  if (error) {
    console.error("Davetiye güncelleme hatası:", error);
    throw error;
  }
}

/**
 * Yatırımcı kaydı
 * @param {object} params
 * @param {string} params.email
 * @param {string} params.password
 * @param {string} params.fullName
 * @param {string} params.phone
 * @param {string} params.country
 * @param {string} params.city
 * @param {string} params.nationality
 * @param {string} params.dateOfBirth
 */
export async function registerInvestor({ email, password, fullName, phone, country, city, nationality, dateOfBirth }) {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: {
        role: "investor",
        full_name: fullName,
        phone: phone || null,
        country: country || null,
        city: city || null,
        nationality: nationality || null,
        date_of_birth: dateOfBirth || null,
      },
    },
  });

  if (error) throw error;
  return data;
}

/**
 * Personel kaydı (davetiye kodu ile)
 * @param {object} params
 * @param {string} params.email
 * @param {string} params.password
 * @param {string} params.fullName
 * @param {string} params.phone
 * @param {string} params.invitationCode
 */
export async function registerStaff({ email, password, fullName, phone, invitationCode }) {
  // 1. Davetiye kodunu doğrula
  const { valid, invitation } = await validateInvitationCode(invitationCode);
  if (!valid || !invitation) {
    throw new Error("INVALID_INVITATION");
  }

  // 2. Kayıt ol
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: {
        role: "staff",
        full_name: fullName,
        phone: phone || null,
      },
    },
  });

  if (error) throw error;

  // 3. Davetiyeyi kullanıldı olarak işaretle
  if (data?.user) {
    try {
      await markInvitationUsed(invitation.id, data.user.id);
    } catch (invErr) {
      console.error("Davetiye güncelleme başarısız:", invErr);
      // Kayıt başarılı oldu ama davetiye güncelleme başarısız
      // Bu durumda kayıt geri alınmaz, admin manuel düzeltir
    }
  }

  return data;
}

/**
 * Giriş yap
 * @param {string} email
 * @param {string} password
 */
export async function loginUser(email, password) {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });

  if (error) throw error;
  return data;
}

/**
 * Çıkış yap
 */
export async function logoutUser() {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

/**
 * Kullanıcı profilini getir
 * @param {string} userId
 */
export async function getUserProfile(userId) {
  const { data, error } = await supabase
    .from("user_profiles")
    .select("*")
    .eq("id", userId)
    .single();

  if (error) throw error;
  return data;
}

/**
 * Yatırımcı profilini getir
 * @param {string} userId
 */
export async function getInvestorProfile(userId) {
  const { data, error } = await supabase
    .from("investor_profiles")
    .select("*")
    .eq("id", userId)
    .single();

  if (error) throw error;
  return data;
}
